<template>
  <div>
    <!-- Waitlist Pop Out -->
    <reservation-results-dialog
      v-if="waitlistDialog"
      visible
      :title="$t('title.reservations_on_wait_list')"
      :reservations="waitlistReservations"
      @close="waitlistDialog = false"
    />

    <!-- Day Note -->
    <day-note-bar
      v-if="daynoteVisible"
      :compact="$vuetify.breakpoint.mdAndDown"
      :on-delete="removeDayNote"
      :on-edit="editDayNote"
    />
    <day-note-edit-dialog
      v-if="dayNoteDialog"
      :day-note="selectedDayNote"
      :visible="dayNoteDialog"
      @confirm="saveSelectedDayNote"
      @close="closeDayNoteDialog"
    />
    <confirmation-dialog
      v-if="deleteDayNoteConfirmation"
      :visible="deleteDayNoteConfirmation"
      :message="$t('message.confirm_delete_daynote')"
      @confirm="removeSelectedDayNote"
      @close="deleteDayNoteConfirmation=false"
    />

    <!-- Tool Bar -->
    <main-toolbar>
      <slot />
      <v-spacer v-if="daynoteVisible || printVisible" />
      <template #buttons.prepend>
        <slot name="buttons.prepend" />

        <v-btn
          v-if="waitlistReservations.length > 0"
          icon
          @click="waitlistClicked"
        >
          <v-badge
            :content="waitlistReservations.length"
            color="purple darken-3"
            overlap
            small
          >
            <v-icon>mdi-account-clock-outline</v-icon>
          </v-badge>
        </v-btn>

        <slot name="buttons" />
        <v-btn
          v-if="printVisible"
          class="pr-0"
          icon
          @click="print"
        >
          <v-icon>mdi-printer-outline</v-icon>
        </v-btn>
        <v-btn
          v-if="daynoteVisible"
          icon
          @click="addDayNote()"
        >
          <v-icon>mdi-message-outline</v-icon>
        </v-btn>
      </template>
      <template #buttons.append>
        <slot name="buttons.append" />
      </template>
    </main-toolbar>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from 'vue';
import DayNoteBar from '@/components/views/main/DayNoteBar.vue';
import DayNoteEditDialog from '@/components/views/main/DayNoteEditDialog.vue';
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';
import DayNote from '@/model/DayNote';
import { performSaveAction, performAction } from '@/services/vue-utils';
import { cloneModel } from '@/model/model-utils';
import Mustache from 'mustache';
import templateGuests from '@/../static/guests.mustache?raw';
import Reservation, { ReservationStatus } from '@/model/Reservation';
import store from '@/store/store';
import i18n from '@/plugins/i18n';
import Contact from '@/model/Contact';
import { mapBySlotToMapByTimeText, toSortedMapBySlot } from '@/services/reservations-utils';
import { useLocalized } from '@/components/helpers/vue2-helpers';
import ReservationResultsDialog from '@/components/views/reservation/ReservationResultsDialog.vue';
import MainToolbar from './MainToolbar.vue';

const localized = useLocalized();

const props = defineProps({
  printVisible: { type: Boolean as PropType<boolean>, required: false, default: true },
  daynoteVisible: { type: Boolean as PropType<boolean>, required: false, default: true },
  reservations: { type: Array as PropType<Array<Reservation>>, required: false, default: () => [] },
  waitlistPopout: { type: Boolean as PropType<boolean>, required: false, default: false },
});

const emit = defineEmits(['click:waitlist']);

// Waitlist popout
const waitlistDialog = ref(false);

const waitlistReservations = computed(() => store.getters.dayReservations
  .filter((r: Reservation) => r.status === ReservationStatus.WaitingList));

const waitlistClicked = () => {
  if (props.waitlistPopout) {
    waitlistDialog.value = true;
    return;
  }
  emit('click:waitlist');
};

// Day Note
const dayNoteDialog = ref(false);
const deleteDayNoteConfirmation = ref(false);
const selectedDayNote = ref(new DayNote());

const removeDayNote = (dayNote: DayNote) => {
  selectedDayNote.value = cloneModel(dayNote);
  delete selectedDayNote.value.text;
  deleteDayNoteConfirmation.value = true;
};

const editDayNote = (dayNote: DayNote) => {
  selectedDayNote.value = cloneModel(dayNote);
  dayNoteDialog.value = true;
};

const addDayNote = () => {
  selectedDayNote.value = new DayNote();
  selectedDayNote.value.date = store.state.update.date;
  dayNoteDialog.value = true;
};

const closeDayNoteDialog = () => { dayNoteDialog.value = false; };

const saveSelectedDayNote = async (dayNote: DayNote) => {
  const ok = await performSaveAction(
    undefined,
    async () => store.dispatch('sendDayNote', { entity: dayNote }),
  );
  if (ok) closeDayNoteDialog();
};

const removeSelectedDayNote = async (dayNote: DayNote) => {
  if (!selectedDayNote.value) return;

  const ok = await performAction(
    i18n.tc('message.success_daynote_deleted'),
    i18n.tc('error.deleting_day_note'),
    async () => store.dispatch('sendDayNote', { entity: selectedDayNote.value }),
  );
};

// Print

class ReservationForExport {
  tab?: string | null;

  partySize: number;

  dateEndText: string | null;

  fixedEnding: boolean;

  contact: Contact;

  status: string;

  label?: string | null;

  service?: string | null;

  isStarred: boolean;

  isFlagged: boolean;

  notes: string | null;

  tabItems: string | null;

  paymentInfo?: string;

  constructor(r: Reservation) {
    this.tab = r.tab?.name;
    this.partySize = r.partySize;
    this.dateEndText = localized.hourMinText(r.dateEnd);
    this.fixedEnding = r.fixedEnding || false;
    this.contact = r.contact;
    this.status = i18n.tc(`code.status.${r.statusText}`);
    this.label = r.label?.text;
    this.service = r.service?.text;
    this.isStarred = r.isStarred || false;
    this.isFlagged = r.isFlagged || false;
    this.notes = '';
    if (r.contact.phone) {
      this.notes = `✆ ${r.contact.phone}`;
    }
    if (r.notes) {
      this.notes = `${this.notes} ${this.notes.length > 0 ? ' | ' : ''}${i18n.t('label.note')}: ${r.notes}`;
    }
    this.tabItems = r.tabItems.map((t) => t.no).join(', ');
    this.paymentInfo = r.paymentInfo;
  }
}

const dayNotes = computed(() => store.getters.dayDayNotes);
const accountName = computed(() => store.state.settings.account.accountName ?? 'N/A');
const dayStats = computed(() => store.getters.dayStats);
const showDayStats = computed(() => store.state.settings.accountSettings.showLunchDinner ?? true);
const filterText = computed(() => {
  const { filter } = store.state;
  let ret = '';
  if (filter.from !== '06:00') {
    ret = `${i18n.tc('label.filter_time_from')}:<b>${localized.hourMinTextFrom24hr(filter.from.toString())}</b>`;
  }
  if (filter.to !== '24:00') {
    ret = `${ret} ${i18n.tc('label.filter_time_until')}:<b>${localized.hourMinTextFrom24hr(filter.to.toString())}</b>`;
  }
  if (filter.tab !== null) {
    ret = `${ret} ${i18n.tc('label.section')}:<b>${filter.tab.name.replace(/ /g, '\u00a0')}</b>`;
  }
  if (filter.status !== ReservationStatus.Valid) {
    ret = `${ret} ${i18n.tc('label.status')}: <b>${filter.status}</b>`;
  }
  return ret;
});

const reservationsByTime = computed(() => {
  if (props.reservations.length === 0) {
    return [];
  }

  const reservationsBySlot = toSortedMapBySlot(props.reservations);
  const reservationsByTimeMap = mapBySlotToMapByTimeText(reservationsBySlot);

  const ret = [] as any;
  reservationsByTimeMap.forEach((value: Reservation[], key: string) => {
    const r = [] as any;
    value.forEach((reservation) => {
      r.push(new ReservationForExport(reservation));
    });
    ret.push({ label: localized.hourMinTextFrom24hr(key), reservations: r });
  });
  return ret;
});

const print = () => {
  const totalGuests = props.reservations.reduce((s, r) => s + r.partySize, 0);
  const tabItems = new Set(props.reservations.flatMap((r) => r.tabItems).map((ti) => ti.id));

  const rendered = Mustache.render(templateGuests, {
    i18n: {
      noReservations: i18n.tc('placeholder.no_reservations'),
      note: i18n.tc('label.note'),
      printedOn: i18n.tc('label.printed_on'),
      noName: i18n.tc('placeholder.no_name'),
      ends: i18n.tc('label.reservation_ending_time'),
      print: i18n.tc('button.print'),
      close: i18n.tc('button.close'),
    },
    printDebug: import.meta.env.VITE_DEBUG_EXPORT_PRINT_DIALOG === 'true',
    reservations: props.reservations,
    reservationsByTime: reservationsByTime.value,
    reportDate: localized.dateText(store.state.update.date),
    printDate: localized.dateTimeText(new Date()),
    filterText: filterText.value,
    accountName: accountName.value,
    stats: i18n.t('label.reservations_x_tables_x_guests_x', [props.reservations.length, tabItems.size, totalGuests]),
    showDayStats: showDayStats.value,
    dayStats: `${i18n.t('label.lunch_dinner')} <b>${dayStats.value.lunch}</b> / <b>${dayStats.value.dinner}</b>`,
    dayNotes: dayNotes.value,
  });

  const newWin = window.open('', 'windowName', 'height=924,width=810');
  newWin!.document.write(rendered);
  newWin!.document.close();
  newWin!.focus();
};

</script>
